import { useState, useEffect } from "react";
import {
  FiEdit,
  FiUser,
  FiMap,
  FiDollarSign,
  FiTag,
  FiBriefcase
} from "react-icons/fi";
import styles from "./CompanyDetailModal.module.css";
import { API_URL } from '../../../config/api';

import CompanyTab from "./TableCompany/CompanyTab";
import EmployeesTab from "./TableCompany/EmployeesTab";
import StoragesTab from "./TableCompany/StoragesTab";
import PurchasesTab from "./TableCompany/PurchasesTab";
import SalesTab from "./TableCompany/SalesTab";
import WalletsTab from "./TableCompany/WalletsTab";
import IntegrationTab from "./TableCompany/IntegrationTab";
import LogsTab from "./TableCompany/LogsTab";
import TradePointTab from "./TableCompany/TradePointTab";
import ContractsTab from "./TableCompany/ContractsTab";

export default function CompanyDetailModal({ open, onClose, companyId }) {
  const [company, setCompany] = useState(null);
  const [categories, setCategories] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [activeTab, setActiveTab] = useState("Компания"); 

  const token = localStorage.getItem("token");

  const handleClose = () => {
    setCompany(null);
    setCategories([]);
    setSpecializations([]);
    setActiveTab("Компания");
    onClose();
  };

  useEffect(() => {
    if (!companyId || !open) return;

    const fetchCompany = async () => {
      try {
        const res = await fetch(`${API_URL}/api/orgs/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCompany(data.data);
      } catch (err) {
        console.error("Ошибка получения компании:", err);
      }
    };

    fetchCompany();
  }, [companyId, open, token]);

  // категории
  useEffect(() => {
    if (!open) return;

    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/org_categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCategories(data.data || []);
      } catch (err) {
        console.error("Ошибка загрузки категорий:", err);
      }
    };

    fetchCategories();
  }, [open, token]);

  // специализации
  useEffect(() => {
    if (!company?.category_id) return;

    const fetchSpecs = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/org_specializations?category_id=${company.category_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setSpecializations(data.data || []);
      } catch (err) {
        console.error("Ошибка загрузки специализаций:", err);
      }
    };

    fetchSpecs();
  }, [company?.category_id, token]);

  if (!open) return null;

  const categoryTitle =
    categories.find((c) => c.id === company?.category_id)?.title || "-";

  const specializationTitle =
    specializations.find((s) => s.id === company?.specialization_id)?.title ||
    "-";

  const infoBlocks = [
    { label: "Название", value: company?.name || "-", icon: <FiUser />, editable: true },
    { label: "БИН", value: company?.sn || "-", icon: <FiTag />, editable: true },
    {
      label: "Сфера деятельности",
      value: categoryTitle,
      icon: <FiBriefcase />,
      editable: true
    },
    {
      label: "Специализация",
      value: specializationTitle,
      icon: <FiBriefcase />,
      editable: true
    },

    { label: "Лицевой счет", value: company?.account || "-", icon: <FiDollarSign />, editable: false },
    { label: "Баланс владельца", value: company?.owner_balance || "-", icon: <FiDollarSign />, editable: false },
    { label: "Сумма в обороте", value: company?.turnover || "-", icon: <FiDollarSign />, editable: false },

    { label: "Город", value: company?.address?.city || "-", icon: <FiMap />, editable: true },
    { label: "Комментарий", value: company?.comment || "-", icon: <FiEdit />, editable: true },
  ];

  const menuButtons = [
    "Компания",
    "Сотрудники",
    "Склады",
    "Покупки",
    "Продажи",
    "Пополнения",
    "Интеграция",
    "Логи",
    "Торговая точка",
    "Договоры",
  ];

  // 🔥 Выбор компонента
  const renderContent = () => {
    switch (activeTab) {
      case "Компания": return <CompanyTab company={company} />;
      case "Сотрудники": return <EmployeesTab companyId={companyId} />;
      case "Склады": return <StoragesTab companyId={companyId} />;
      case "Покупки": return <PurchasesTab companyId={companyId} />;
      case "Продажи": return <SalesTab companyId={companyId} />;
      case "Пополнения": return <WalletsTab companyId={companyId} />;
      case "Интеграция": return <IntegrationTab companyId={companyId} />;
      case "Логи": return <LogsTab companyId={companyId} />;
      case "Торговая точка": return <TradePointTab companyId={companyId} />;
      case "Договоры": return <ContractsTab companyId={companyId} />;
      default: return null;
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>Детали компании</h2>
          <button className={styles.closeBtn} onClick={handleClose}>×</button>
        </div>

        <div className={styles.topBlock}>
          {infoBlocks.map((b, idx) => (
            <div key={idx} className={styles.infoCard}>
              <div className={styles.icon}>{b.icon}</div>

              <div className={styles.infoText}>
                <strong>{b.label}</strong>
                <p>{b.value}</p>
              </div>

              {b.editable && (
                <button className={styles.editBtn}>
                  <FiEdit />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className={styles.menuBlock}>
          {menuButtons.map((btn, idx) => (
            <button
              key={idx}
              onClick={() => setActiveTab(btn)}
              className={`${styles.menuBtn} ${activeTab === btn ? styles.active : ""}`}
            >
              {btn}
            </button>
          ))}
        </div>

        <div className={styles.content}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
